import { useCallback, useEffect, useMemo, useState } from "react";
import { ExemptedStudent } from "../../types/common";
import { ExemptionSectionProps } from "./ExemptionSection";
import { SortConfig } from "../../core";
import { useTableRowActions } from "../proctoring-options/hooks/useEditableList";

export type ExemptionInfo = {
	nonExemptRoster: Map<number, ExemptedStudent>;
	exemptRoster: Map<number, ExemptedStudent>;
};

/** Adds or updates accommodations for a set of students */
export function updateStudentsInRoster(
	info: ExemptionInfo,
	selectedStudents: ExemptedStudent[],
	profileId: number | undefined,
	notes: string | undefined,
) {
	const updatedExemptRoster = new Map(info.exemptRoster);
	const updatedNonExemptRoster = new Map(info.nonExemptRoster);

	selectedStudents.forEach((student) => {
		updatedNonExemptRoster.delete(student.userId);

		const updatedStudent = {
			...student,
			proctoringOptionProfileId: profileId,
			accommodationNotes: notes,
		};

		updatedExemptRoster.set(student.userId, updatedStudent);
	});

	return {
		nonExemptRoster: updatedNonExemptRoster,
		exemptRoster: updatedExemptRoster,
	};
}

/** Removes accommodations and moves students back to the non-exempt roster */
export const removeStudentsFromExemptRoster = (info: ExemptionInfo, studentIds: number[]): ExemptionInfo => {
	const updatedExemptRoster = new Map(info.exemptRoster);
	const updatedNonExemptRoster = new Map(info.nonExemptRoster);

	studentIds.forEach((id) => {
		const student = updatedExemptRoster.get(id);
		if (!student) return;

		updatedExemptRoster.delete(id);

		const resetStudent = {
			...student,
			proctoringOptionProfileId: undefined,
			accommodationNotes: undefined,
		};

		updatedNonExemptRoster.set(id, resetStudent);
	});

	return {
		nonExemptRoster: updatedNonExemptRoster,
		exemptRoster: updatedExemptRoster,
	};
};

export function getProfileColor(profileName: string): string {
	const colors = ["#D3DDF2", "#F9E4F5", "#E4E3F6", "#F8E4DD", "#FAEFDE", "#DFF2F2", "#F8DCDB", "#DBE7E1", "#E6E6E6"];
	const index = profileName.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
	return colors[index];
}

export const ACCOMMODATION_FILTER = ["all", "notAccommodated", "notProctored", "modifiedProctoring"] as const;
export type AccommodationFilterType = (typeof ACCOMMODATION_FILTER)[number];

type UseAccommodationFilterProps = {
	profilesMap: ExemptionSectionProps["profilesMap"];
	exemptionInfo: ExemptionSectionProps["exemptionInfo"];
	recentIds: Set<number>;
};

export function useAccommodationFilter({ profilesMap, exemptionInfo, recentIds }: UseAccommodationFilterProps) {
	const [searchFilter, setSearchFilter] = useState("");
	const [activeStatus, setActiveStatus] = useState<AccommodationFilterType>("all");

	const [sortConfig, setSortConfig] = useState<SortConfig<ExemptedStudent>[]>([{ key: "name", direction: "asc" }]);

	const combinedRoster = useMemo(() => {
		// Merge both maps into a single array
		const allStudents = [
			...Array.from(exemptionInfo.nonExemptRoster.values()),
			...Array.from(exemptionInfo.exemptRoster.values()),
		];

		return allStudents;
	}, [exemptionInfo]);

	const filteredRoster = useMemo(() => {
		const filtered = combinedRoster.filter((student) => {
			// Search Filter
			const matchesSearch = student.name.toLowerCase().includes(searchFilter.toLowerCase()) || !searchFilter;

			// Status Filter
			let matchesStatus = true;
			if (activeStatus === "notAccommodated") {
				matchesStatus =
					student.proctoringOptionProfileId === undefined && !exemptionInfo.exemptRoster.has(student.userId);
			} else if (activeStatus === "notProctored") {
				matchesStatus =
					student.proctoringOptionProfileId === undefined && exemptionInfo.exemptRoster.has(student.userId);
			} else if (activeStatus === "modifiedProctoring") {
				// If it's a profile ID
				matchesStatus =
					student.proctoringOptionProfileId !== undefined && exemptionInfo.exemptRoster.has(student.userId);
			}

			return matchesSearch && matchesStatus;
		});

		return [...filtered].sort((a, b) => {
			// Put recent changes at the top
			const aIsRecent = recentIds.has(a.userId);
			const bIsRecent = recentIds.has(b.userId);

			if (aIsRecent && !bIsRecent) return -1;
			if (!aIsRecent && bIsRecent) return 1;

			const activeSort = sortConfig[sortConfig.length - 1];

			// If no sort is selected, or we are doing a tie-breaker, default to Name Asc
			const key = activeSort?.key || "name";
			const direction = activeSort?.direction || "asc";
			const mult = direction === "asc" ? 1 : -1;

			let comparison = 0;

			if (key === "proctoringOptionProfileId") {
				const getStatusSortValue = (student: ExemptedStudent) => {
					const isExempted = exemptionInfo.exemptRoster.has(student.userId);
					// Use numbers to ensure consistent grouping
					if (!isExempted) return "0";
					if (!student.proctoringOptionProfileId) return "1";
					const profile = profilesMap.get(student.proctoringOptionProfileId);
					return `2_${profile?.name || ""}`;
				};
				comparison = getStatusSortValue(a).localeCompare(getStatusSortValue(b));
			} else {
				// Default sort for Name
				const aVal = (a[key] ?? "").toString().toLowerCase();
				const bVal = (b[key] ?? "").toString().toLowerCase();
				if (aVal < bVal) comparison = -1;
				if (aVal > bVal) comparison = 1;
			}

			// TIE BREAKER: If values are the same, always sort by name alphabetically
			if (comparison === 0 && key !== "name") {
				return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
			}

			return comparison * mult;
		});
	}, [combinedRoster, searchFilter, activeStatus, exemptionInfo.exemptRoster, recentIds, sortConfig, profilesMap]);

	const clearFilters = useCallback(() => {
		setSearchFilter("");
		setActiveStatus("all");
	}, []);

	return {
		filteredRoster,
		sortConfig,
		setSortConfig,
		clearFilters,
		searchFilter,
		setSearchFilter,
		activeStatus,
		setActiveStatus,
	};
}

const PAGE_SIZE = 4;

export function usePaginatedRows(students: ExemptedStudent[], curPage: number, setCurPage: (page: number) => void) {
	const totalPages = Math.ceil(students.length / PAGE_SIZE);

	useEffect(() => {
		if (curPage > totalPages) {
			setCurPage(totalPages);
		}
	}, [curPage, setCurPage, totalPages]);

	const paginatedRows = useMemo(() => {
		const startIdx = (curPage - 1) * PAGE_SIZE;
		return students.slice(startIdx, startIdx + PAGE_SIZE);
	}, [curPage, students]);

	return { paginatedRows, setCurPage, curPage, totalPages };
}

export function useStudentSelection(filteredRoster: ExemptedStudent[], exemptionInfo: ExemptionInfo) {
	const { selectedIds, toggleSelectOne, clearSelection } = useTableRowActions();
	const selectedStudents = useMemo(() => {
		return filteredRoster.filter((student) => selectedIds.has(String(student.userId)));
	}, [filteredRoster, selectedIds]);

	const allSelected = useMemo(
		() => selectedIds.size === filteredRoster.length && filteredRoster.length > 0,
		[filteredRoster.length, selectedIds.size],
	);
	const isIndeterminate = useMemo(
		() => selectedIds.size > 0 && selectedIds.size < filteredRoster.length,
		[filteredRoster.length, selectedIds.size],
	);

	const handleSelectAll = useCallback(
		(checked: boolean) => {
			if (isIndeterminate || !checked) {
				clearSelection();
			} else {
				filteredRoster.filter((s) => s.type !== "institution").map((s) => toggleSelectOne(String(s.userId), true));
			}
		},
		[clearSelection, filteredRoster, isIndeterminate, toggleSelectOne],
	);

	const isStudentExempted = useCallback(
		(id: ExemptedStudent["userId"]) => {
			return Boolean(exemptionInfo.exemptRoster.has(id));
		},
		[exemptionInfo.exemptRoster],
	);

	const isAllSelectedNoAccommodation = useMemo(() => {
		return selectedStudents.every((s) => !isStudentExempted(s.userId));
	}, [isStudentExempted, selectedStudents]);

	const canRemoveAccommodations = useMemo(
		() => Array.from(selectedIds).some((id) => isStudentExempted(Number(id))),
		[isStudentExempted, selectedIds],
	);

	return {
		selectedStudents,
		selectedIds,
		clearSelection,
		allSelected,
		handleSelectAll,
		isIndeterminate,
		toggleSelectOne,
		isStudentExempted,
		isAllSelectedNoAccommodation,
		canRemoveAccommodations,
	};
}

type UseStudentSelectionReturn = ReturnType<typeof useStudentSelection>;

export function useExemptionModals(
	profilesMap: ExemptionSectionProps["profilesMap"],
	selectedStudents: UseStudentSelectionReturn["selectedStudents"],
) {
	const [selectAccommodationModalShowing, setSelectAccommodationModalShowing] = useState(false);
	const [viewDetailsModalShowing, setViewDetailsModalShowing] = useState(false);
	const [currentStudent, setCurrentStudent] = useState<ExemptedStudent>();

	const openAccommodationModal = useCallback(() => setSelectAccommodationModalShowing(true), []);
	const closeAccommodationModal = useCallback(() => setSelectAccommodationModalShowing(false), []);

	const openViewModal = useCallback((student: ExemptedStudent) => {
		setCurrentStudent(student);
		setViewDetailsModalShowing(true);
	}, []);

	const closeViewModal = useCallback(() => setViewDetailsModalShowing(false), []);

	const curStudentProfile = useMemo(
		() =>
			currentStudent && currentStudent.proctoringOptionProfileId
				? profilesMap.get(currentStudent.proctoringOptionProfileId)
				: undefined,
		[currentStudent, profilesMap],
	);

	const accommodationModalProfileId = useMemo(() => {
		return (selectedStudents && selectedStudents.length > 0
			? selectedStudents.map((student) => student.proctoringOptionProfileId).filter((id) => id !== undefined)
			: []
		).length === 1
			? selectedStudents[0].proctoringOptionProfileId
			: undefined;
	}, [selectedStudents]);

	const accommodationNotes = useMemo(() => {
		return selectedStudents && selectedStudents.length > 0
			? selectedStudents.every((student) => student.accommodationNotes === selectedStudents[0].accommodationNotes)
				? selectedStudents[0].accommodationNotes
				: undefined
			: undefined;
	}, [selectedStudents]);

	return {
		viewModal: {
			close: closeViewModal,
			open: openViewModal,
			isOpen: viewDetailsModalShowing,
		},
		accommodationModal: {
			close: closeAccommodationModal,
			open: openAccommodationModal,
			isOpen: selectAccommodationModalShowing,
			notes: accommodationNotes,
			profileId: accommodationModalProfileId,
		},
		curStudentProfile,
		currentStudent,
	};
}
